import { useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import Sidebar from "../components/Sidebar";
import { db } from "../firebase";

const statusOptions = ["", "Received", "Validating", "Generating", "Verifying", "Waiting Approval", "Approved", "Sending Email", "Completed", "Failed", "Manual Review"];
const clients = ["", "CPBM", "ARSL", "AE", "ATL", "ES"];

function createdTime(item) {
  if (item.createdAt?.toMillis) return item.createdAt.toMillis();
  if (item.receivedTime) return new Date(item.receivedTime).getTime() || 0;
  return 0;
}

function formatDate(value) {
  if (!value) return "";
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB");
}

function statusClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized.includes("failed")) return "danger";
  if (normalized.includes("manual")) return "warning";
  if (normalized.includes("completed") || normalized.includes("approved")) return "success";
  return "";
}

export default function FinanceAIEmployee() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: "", client: "", status: "", date: "" });

  const loadJobs = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(query(collection(db, "finance_ai_jobs"), orderBy("createdAt", "desc")));
      setJobs(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).sort((a, b) => createdTime(b) - createdTime(a)).slice(0, 200));
    } catch (error) {
      alert(`Finance AI jobs could not be loaded: ${error.message}`);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const filteredJobs = jobs.filter((job) => {
    const searchText = [job.jobId, job.client, job.sender, job.subject, job.status, ...(job.invoiceNumbers || [])].filter(Boolean).join(" ").toLowerCase();
    const receivedDate = String(job.receivedTime || "").slice(0, 10);
    return (!filters.search || searchText.includes(filters.search.toLowerCase()))
      && (!filters.client || job.client === filters.client)
      && (!filters.status || job.status === filters.status)
      && (!filters.date || receivedDate === filters.date);
  });

  const metrics = useMemo(() => {
    const todayKey = new Date().toISOString().slice(0, 10);
    return {
      today: jobs.filter((job) => String(job.receivedTime || "").slice(0, 10) === todayKey).length,
      waiting: jobs.filter((job) => job.status === "Waiting Approval").length,
      completed: jobs.filter((job) => job.status === "Completed").length,
      failed: jobs.filter((job) => job.status === "Failed").length,
      manual: jobs.filter((job) => job.status === "Manual Review").length
    };
  }, [jobs]);

  const updateJobStatus = async (job, status) => {
    await updateDoc(doc(db, "finance_ai_jobs", job.id), {
      status,
      updatedAt: serverTimestamp(),
      logs: [
        ...(job.logs || []),
        { at: new Date().toISOString(), level: "info", message: `Finance marked ${status}` }
      ]
    });
    await loadJobs();
  };

  return (
    <div className="app-shell finance-ai-page">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Allied Automation</div>
            <h1 className="page-title">Finance AI Agent</h1>
            <p className="page-copy">Monitor scheduled and email-driven invoice jobs. The CPBM FTMO invoice is generated for the previous month and sent automatically on the first day of each month.</p>
          </div>
          <button className="btn btn-muted" type="button" onClick={loadJobs} disabled={loading}>{loading ? "Loading..." : "Refresh"}</button>
        </header>

        <section className="card-grid finance-ai-metrics">
          <div className="metric-card"><div className="metric-label">Emails Today</div><div className="metric-value">{metrics.today}</div></div>
          <div className="metric-card"><div className="metric-label">Waiting Approval</div><div className="metric-value">{metrics.waiting}</div></div>
          <div className="metric-card"><div className="metric-label">Completed</div><div className="metric-value">{metrics.completed}</div></div>
          <div className="metric-card"><div className="metric-label">Failed</div><div className="metric-value">{metrics.failed}</div></div>
          <div className="metric-card"><div className="metric-label">Manual Review</div><div className="metric-value">{metrics.manual}</div></div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Recent Jobs</h2>
              <p className="page-copy" style={{ marginTop: 4 }}>Search by sender, subject, client, status, or invoice number.</p>
            </div>
            <span className="badge">{filteredJobs.length} jobs</span>
          </div>
          <div className="panel-body filter-grid">
            <input className="input" placeholder="Search jobs" value={filters.search} onChange={(event) => setFilters({ ...filters, search: event.target.value })} />
            <select className="input" value={filters.client} onChange={(event) => setFilters({ ...filters, client: event.target.value })}>{clients.map((client) => <option key={client} value={client}>{client || "All clients"}</option>)}</select>
            <select className="input" value={filters.status} onChange={(event) => setFilters({ ...filters, status: event.target.value })}>{statusOptions.map((status) => <option key={status} value={status}>{status || "All statuses"}</option>)}</select>
            <input className="input" type="date" value={filters.date} onChange={(event) => setFilters({ ...filters, date: event.target.value })} />
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Sender</th>
                  <th>Subject</th>
                  <th>Received</th>
                  <th>Invoices</th>
                  <th>PDFs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job) => (
                  <tr key={job.id}>
                    <td>{job.client || "-"}</td>
                    <td><span className={`status-pill ${statusClass(job.status)}`}>{job.status || "Received"}</span></td>
                    <td>{job.sender || "-"}</td>
                    <td>{job.subject || "-"}</td>
                    <td>{formatDate(job.receivedTime || job.createdAt)}</td>
                    <td>{job.invoiceCount || 0}</td>
                    <td>{job.pdfCount || 0}</td>
                    <td>
                      <div className="table-actions">
                        {job.status === "Waiting Approval" && <button className="btn btn-success" type="button" onClick={() => updateJobStatus(job, "Approved")}>Approve</button>}
                        {job.status !== "Failed" && <button className="btn btn-danger" type="button" onClick={() => updateJobStatus(job, "Failed")}>Reject</button>}
                        {(job.status === "Failed" || job.status === "Manual Review") && <button className="btn btn-muted" type="button" onClick={() => updateJobStatus(job, "Received")}>Restart</button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {!filteredJobs.length && (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: 28 }}>No Finance AI jobs found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
